const categoryMap = {
  M: 'Mini',
  N: 'Mini Elite',
  E: 'Economy',
  H: 'Economy Elite',
  C: 'Compact',
  D: 'Compact Elite',
  I: 'Intermediate',
  J: 'Intermediate Elite',
  S: 'Standard',
  R: 'Standard Elite',
  F: 'Fullsize',
  G: 'Fullsize Elite',
  P: 'Premium',
  U: 'Premium Elite',
  L: 'Luxury',
  W: 'Luxury Elite',
  O: 'Oversize',
  X: 'Special',
};

const typeMap = {
  B: '2-3 Door',
  C: '2/4 Door',
  D: '4-5 Door',
  W: 'Wagon/Estate',
  V: 'Passenger Van',
  L: 'Limousine',
  S: 'Sport',
  T: 'Convertible',
  F: 'SUV',
  J: 'Open Air All Terrain',
  X: 'Special',
  P: 'Pick up Regular Cab',
  Q: 'Pick up Extended Cab',
  Z: 'Special Offer Car',
  E: 'Coupe',
  M: 'Monospace',
  R: 'Recreational Vehicle',
  H: 'Motor Home',
  Y: '2 Wheel Vehicle',
  N: 'Roadster',
  G: 'Crossover',
  K: 'Commercial Van/Truck',
};

const transmissionDrivenWheelsMap = {
  M: 'Manual (drive unspecified)',
  N: 'Manual 4WD',
  C: 'Manual AWD',
  A: 'Auto (drive unspecified)',
  B: 'Auto 4WD',
  D: 'Auto AWD',
};

const fuelOrAirconMap = {
  R: 'Unspecified Fuel With Air Conditioning (AC)',
  N: 'Unspecified Fuel Without AC',
  D: 'Diesel With AC',
  Q: 'Diesel Without AC',
  H: 'Hybrid With AC',
  I: 'Hybrid Without AC',
  E: 'Electric With AC',
  C: 'Electric Without AC',
  L: 'LPG/Compressed Gas With AC',
  S: 'LPG/Compressed Gas Without AC',
  A: 'Hydrogen With AC',
  B: 'Hydrogen Without AC',
  M: 'Multi Fuel/Power With AC',
  F: 'Multi Fuel/Power Without AC',
  V: 'Petrol With AC',
  Z: 'Petrol Without AC',
  U: 'Ethanol With AC',
  X: 'Ethanol Without AC',
};

type AcrissClass = 'category' | 'type' | 'transmission' | 'fuelOrAircon';

const getMapper = (acrissClass: AcrissClass): { [key: string]: string } => {
  if (acrissClass === 'category') {
    return categoryMap;
  }
  if (acrissClass === 'type') {
    return typeMap;
  }
  if (acrissClass === 'transmission') {
    return transmissionDrivenWheelsMap;
  }
  if (acrissClass === 'fuelOrAircon') {
    return fuelOrAirconMap;
  }
  return {};
};

export class AcrissCode {
  acrissClass: AcrissClass;
  code: string;
  description: string;

  constructor(acrissClass: AcrissClass, code: string) {
    this.acrissClass = acrissClass;
    const mapper = getMapper(acrissClass);
    this.code = code;
    this.description = mapper[code];
  }
}

/** The filter id of the search API that each ACRISS code stands for. */
const categoryFilterIds: Record<string, string> = {
  O: 'car_class_premium',
  X: 'car_class_premium',
  P: 'car_class_premium',
  U: 'car_class_premium',
  L: 'car_class_premium',
  W: 'car_class_premium',
  M: 'car_class_small',
  N: 'car_class_small',
  E: 'car_class_small',
  H: 'car_class_small',
  C: 'car_class_compact',
  D: 'car_class_compact',
  I: 'car_class_middle',
  J: 'car_class_middle',
  F: 'car_class_upper',
  S: 'car_class_middle',
  R: 'car_class_middle',
  G: 'car_class_upper',
};

const typeFilterIds: Record<string, string> = {
  T: 'car_type_convertible',
  W: 'car_type_wagon_estate',
  Y: 'car_type_wagon_estate',
  V: 'car_type_van_monospace',
  M: 'car_type_van_monospace',
  K: 'car_class_transporter',
  F: 'car_type_suv_crossover',
  J: 'car_type_suv_crossover',
};

const transmissionFilterIds: Record<string, string> = {
  A: 'automatic',
  C: 'four_wheel_drive',
  N: 'four_wheel_drive',
  B: 'four_wheel_drive',
  D: 'four_wheel_drive',
};

const fuelFilterIds: Record<string, string> = {
  E: 'electric_or_hybrid',
  C: 'electric_or_hybrid',
  H: 'electric_or_hybrid',
  I: 'electric_or_hybrid',
};

export class AcrissFilter {
  private readonly categories: AcrissCode[];
  private readonly types: AcrissCode[];
  private readonly transmissions: AcrissCode[];
  private readonly fuels: AcrissCode[];

  constructor(acrissFilter: string) {
    const acrissCodeLists = acrissFilter
      .split(':')
      .map(string => string.split('|'));

    const [categories, types, transmissionDrive, fuelAircon] = acrissCodeLists;
    this.categories = categories.map(
      category => new AcrissCode('category', category),
    );
    this.types = types.map(type => new AcrissCode('type', type));
    this.transmissions = transmissionDrive.map(
      item => new AcrissCode('transmission', item),
    );
    this.fuels = fuelAircon.map(fuel => new AcrissCode('fuelOrAircon', fuel));
  }

  public filterIds(): string[] {
    const classes: [AcrissCode[], Record<string, string>][] = [
      [this.categories, categoryFilterIds],
      [this.types, typeFilterIds],
      [this.fuels, fuelFilterIds],
      [this.transmissions, transmissionFilterIds],
    ];

    return classes
      .map(([codes, filterIds]) =>
        codes.map(({ code }) => filterIds[code]).find(Boolean),
      )
      .filter(filterId => filterId !== undefined);
  }
}
